package com.klantenstop.web.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import com.klantenstop.web.auth.AuthModule
import com.klantenstop.web.data.models.domain.Radius
import com.klantenstop.web.data.models.domain.SearchParams
import com.klantenstop.web.data.models.dto.responses.moderation.ModerationEntityType
import com.klantenstop.web.utils.AnalyticsManager
import com.klantenstop.web.utils.encodeReturnUrl
import com.klantenstop.web.utils.toCitySlug
import com.materialkobweb.components.widgets.DsSpinner
import com.varabyte.kobweb.browser.uri.encodeURIComponent
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.open
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch

sealed class Navigation(val route: String) {

    sealed class Screen(route: String) : Navigation(route) {

        sealed class Admin(route: String) : Screen("/admin$route") {
            data object Home : Admin("")

            sealed class Categories(route: String) : Admin("/categorieen$route") {
                data object Index : Categories("")
                data class Manage(val id: String? = null) : Categories("/beheren" + if (id != null) "?id=$id" else "")
            }

            sealed class CuratedCities(route: String) : Admin("/steden$route") {
                data object Index : CuratedCities("")
                data class Manage(val id: String? = null) : CuratedCities("/beheren" + if (id != null) "?id=$id" else "")
            }

            sealed class Companies(route: String) : Admin("/bedrijven$route") {
                data object Index : Companies("")
                data class Manage(val id: String? = null) : Companies("/beheren" + if (id != null) "?id=$id" else "")
            }

            data class ModerationQueue(val type: ModerationEntityType) : Admin("/moderatie?type=${type.name.lowercase()}")

            data object Settings : Admin("/instellingen")
        }

        data object Home : Screen("/")
        data object Categories : Screen("/categorieen")
        data class Search(val params: SearchParams = SearchParams(), val page: Int = 1) : Screen(buildSearchRoute(params, page))

        data class CompanyDetail(val companyIdentifier: String) : Screen("/bedrijf/$companyIdentifier")
        data class ManageCompany(val companyId: String = "") : Screen("/bedrijven/beheren/$companyId")
        data object About : Screen("/over-ons")
        data object PatientStopWaitlist : Screen("/patientenstop-wachtlijst")
        data object Contact : Screen("/contact")
        data object PrivacyPolicy : Screen("/privacybeleid")
        data object TermsAndConditions : Screen("/algemene-voorwaarden")
        data class Login(val returnTo: String? = null) : Screen("/inloggen" + if (returnTo != null) "?terugNaar=${encodeReturnUrl(returnTo)}" else "")
        data class Register(val returnTo: String? = null) : Screen("/registreren" + if (returnTo != null) "?terugNaar=${encodeReturnUrl(returnTo)}" else "")
        data object ForgotPassword : Screen("/wachtwoord-vergeten")
        data object RegisterCompany : Screen("/bedrijf-registreren")
        data object Profile : Screen("/profiel")
        data object Settings : Screen("/instellingen")
    }

    sealed class External(route: String) : Navigation(route) {

        data class Maps(val query: String) : Navigation("https://maps.google.com/maps?q=$query")
        data class Mail(val email: String) : Navigation("mailto:${encodeURIComponent(email.trim())}")
        data class Phone(val number: String) : Navigation("tel:$number")
    }
}

data class SectionItem(val id: String, val title: String, val path: String)

/**
 * Builds the path-based `/zoeken` route for [Navigation.Screen.Search]
 *
 * With the following non-canonical query suffixes:
 * `straal` only present when it differs from the default radius AND a location was actually searched.
 * `pagina` reflecting the current results page, only present beyond page 1
 *
 * This keeps the current page in the URL (survives a refresh and is bookmarkable/shareable) without the page number
 * being considered part of the "identity" of a search for routing purposes. `pagina` is the one query suffix that is
 * reflected in the page's canonical URL, since each results page lists different companies.
 *
 * The category path segment is only ever a category: a city-only search (no category selected) has
 * no dedicated path segment of its own and falls back to the bare "/zoeken" route, consistent with
 * how coordinates are never reflected in the URL either. This keeps the segment unambiguous -- e.g.
 * navigating to "/zoeken/bocholt" is never mistaken for a city search just because "bocholt" isn't
 * a known category slug.
 */
private fun buildSearchRoute(params: SearchParams, page: Int = 1): String {
    val categorySlug = params.category?.publicIdentifier
    val citySlug = params.toCitySlug()

    val path = when {
        categorySlug != null && citySlug != null -> "/zoeken/$categorySlug/$citySlug"
        categorySlug != null -> "/zoeken/$categorySlug"
        else -> "/zoeken"
    }

    val queryParams = buildList {
        if (citySlug != null && params.radius != Radius.Km5) add("straal=${params.radius.range}")
        if (page > 1) add("pagina=$page")
    }

    return if (queryParams.isEmpty()) path else "$path?${queryParams.joinToString("&")}"
}

private val logoutScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

fun logout(doNavigation: Boolean = true) = logoutScope.launch {
    // Reset PostHog session BEFORE clearing auth state so the event can still be sent
    AnalyticsManager.trackEvent(AnalyticsManager.Events.LOGOUT)
    AnalyticsManager.resetSession()

    // Call backend to clear HTTP-only cookies
    AuthModule.logout()

    if (doNavigation) {
        window.open(Navigation.Screen.Home.route, OpenLinkStrategy.IN_PLACE)
    }
}

// Guard to protect routes that should only really be accessible to non-authenticated users
// e.g. a logged in user can't really benefit of the login and register pages, so they should be redirected to the home page if they try to access those routes while being logged in
@Composable
fun unauthenticatedGuard(content: @Composable () -> Unit) {
    val authStatus = AuthModule.authStatus.collectAsState()
    val isAuthenticated = authStatus.value?.authenticated

    when (isAuthenticated) {
        true -> {
            // If the user is already logged in, redirect to the home page
            val pageContext = rememberPageContext()
            LaunchedEffect(Unit) {
                pageContext.router.navigateTo(
                    pathQueryAndFragment = Navigation.Screen.Home.route,
                    updateHistoryMode = UpdateHistoryMode.REPLACE
                )
            }
        }

        null -> {
            // Still loading, show a spinner or something
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                DsSpinner()
            }
        }

        else -> content()
    }
}

// Guard to protect routes that require authentication
// If user is not logged in, redirect to login page with returnTo parameter
@Composable
fun authenticatedGuard(content: @Composable () -> Unit) {
    val authStatus = AuthModule.authStatus.collectAsState()
    val isAuthenticated = authStatus.value?.authenticated

    val pageContext = rememberPageContext()

    when (isAuthenticated) {
        false -> {
            // User is not authenticated, redirect to login with return URL
            LaunchedEffect(Unit) {
                pageContext.router.navigateTo(
                    pathQueryAndFragment = Navigation.Screen.Login(
                        returnTo = pageContext.route.pathQueryAndFragment
                    ).route,
                    updateHistoryMode = UpdateHistoryMode.REPLACE
                )
            }
        }

        null -> {
            // Still loading, show a spinner
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                DsSpinner()
            }
        }

        else -> content()
    }
}
